import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { useAdmin } from '../contexts/AdminContext';
import { onboardingManager } from '../business/onboarding/OnboardingManager';
import { UserRoleConfigManager } from '../business/users/UserRoleConfig';

interface OnboardingStatus {
  isChecking: boolean;
  needsOnboarding: boolean;
}

/**
 * Single source of truth for whether the current user needs onboarding.
 * Only super-users on a tenant are gated (never host admins or other roles).
 * Progress is read via the cached onboardingManager.getProgress() so the few
 * consumers (AppInitializer, Navbar) share one request. Fails OPEN — if
 * progress is unknown or the fetch errors, an already-onboarded tenant is never
 * prompted by mistake.
 */
export function useOnboardingStatus(): OnboardingStatus {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useAppConfig();
  const { isHostAdmin, isLoading: adminLoading } = useAdmin();

  const [isChecking, setIsChecking] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (authLoading || configLoading || adminLoading) return;
    if (!isAuthenticated) { setIsChecking(false); return; }

    const tenantId = config?.currentUser?.tenantId;
    if (isHostAdmin || !tenantId) { setIsChecking(false); return; }

    const roleType = UserRoleConfigManager.determineRoleType(config?.currentUser?.roles || []);
    if (roleType !== 'super_user') { setIsChecking(false); return; }

    if (checkedRef.current) return;
    checkedRef.current = true;

    onboardingManager.getProgress()
      .then(p => setNeedsOnboarding(!(p.isCompleted ?? true)))
      .catch(() => setNeedsOnboarding(false))
      .finally(() => setIsChecking(false));
  }, [isAuthenticated, authLoading, configLoading, adminLoading, isHostAdmin, config]);

  return { isChecking, needsOnboarding };
}
